import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";

const AuthContext = createContext(null);
const STORAGE_KEY = "flowup_auth_token";
const PREF_KEY = "flowup_remember_me";  // "1" → localStorage (persistent)

/** Read the token from whichever storage was used at login.
 *  Order: localStorage (remember-me) → sessionStorage (single-session). */
function readToken() {
  return localStorage.getItem(STORAGE_KEY) || sessionStorage.getItem(STORAGE_KEY) || "";
}

/** Write the token to the right storage based on the user's "remember me"
 *  choice. Always clears the OTHER storage so we never end up with two
 *  conflicting tokens. */
function writeToken(token, remember) {
  if (remember) {
    localStorage.setItem(STORAGE_KEY, token);
    localStorage.setItem(PREF_KEY, "1");
    sessionStorage.removeItem(STORAGE_KEY);
  } else {
    sessionStorage.setItem(STORAGE_KEY, token);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(PREF_KEY);
  }
}

function clearToken() {
  localStorage.removeItem(STORAGE_KEY);
  sessionStorage.removeItem(STORAGE_KEY);
}

/** Stores JWT in localStorage (remember-me) or sessionStorage (single-session)
 * and exposes it via context.
 *
 * Token is also attached to every axios call via the interceptor in `lib/api.js`.
 * Backend additionally accepts the same token through an httpOnly cookie set
 * by /auth/login, but the frontend only ever reads from localStorage.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user || null);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = readToken();
    if (token) {
      fetchMe();
      return;
    }
    // Owner spec 2026-07-08w — no login screen at all. When
    // DEV_AUTH_BYPASS=1 on the backend, auto-fetch a super-admin
    // session on boot so ANY route (including Full Frame) opens
    // without the user seeing a login gate. Skip on the production
    // hostname so the real site still requires credentials.
    const host = (typeof window !== "undefined" ? window.location.hostname : "").toLowerCase();
    const isProductionHost = host === "flow-up.co.il" || host === "www.flow-up.co.il";
    if (isProductionHost) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data: status } = await api.get("/auth/dev-bypass-status");
        if (cancelled) return;
        if (!status?.active) { setLoading(false); return; }
        const { data } = await api.post("/auth/dev-bypass");
        if (cancelled || !data?.token) { setLoading(false); return; }
        writeToken(data.token, true);
        setUser(data.user);
      } catch {
        /* fall through — user stays unauthenticated */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [fetchMe]);

  const login = async (email, password, remember = true) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Always persist to localStorage so the user stays logged-in across
    // browser restarts. The `remember` argument is kept for API parity
    // but ignored unless the caller explicitly opted out.
    writeToken(data.token, remember !== false);
    setUser(data.user);
    return data.user;
  };

  const register = async ({ email, password, full_name, phone, company_name, company_logo }) => {
    const { data } = await api.post("/auth/register", {
      email, password, full_name, phone, company_name, company_logo,
    });
    writeToken(data.token, true);  // new accounts auto-remember
    setUser(data.user);
    return { user: data.user, slug: data.slug, company: data.company };
  };

  // Exchange an Emergent OAuth `session_id` for our JWT.  Used by the
  // /auth/callback page after Google sign-in.
  const googleSession = async (sessionId) => {
    const { data } = await api.post("/auth/google/session", { session_id: sessionId });
    writeToken(data.token, true);  // Google logins auto-remember (they re-auth via OAuth anyway)
    setUser(data.user);
    return { user: data.user, isNew: !!data.is_new };
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* no-op */ }
    clearToken();
    setUser(null);
  };

  // Bypass-only escape hatch — lets a caller install a session from a
  // token+user pair without re-running login(). Used by the DEV_AUTH_BYPASS
  // path on LoginPage. Keep public surface tiny so prod usage is hard
  // to write by accident.
  const setSession = (userObj, token, remember = true) => {
    writeToken(token, remember);
    setUser(userObj);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, googleSession, logout, setSession, refresh: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

export function getStoredToken() {
  return readToken();
}
